// Considered: `SphereTimelineEventMolecule`, `HomeMode`, `CompactTimelineMolecule` — these are
// what `TrajectoryPanelOrganism` retires; none carry a per-track card shell. See spec Reuse Audit
// (specs/stream-trajectory-tools.md § New components — justified).
//
// Source: design/metaspace-tools/metaspace-trajectory-v2.jsx:3-21 (`MSTrackCard`). Title
// (inspectable) · pin · mono readout, plus a `footer?` slot for cadence-bearing tracks (the
// caption + in-place check-in control below the body).

import React from 'react';
import { useTranslation } from 'localization';
import InspectableMolecule from '../Inspectable/InspectableMolecule';
import GlyphAtom from '../../atoms/Glyph/GlyphAtom';
import type { InspectorPayload } from 'types/inspector';
import './TrackCardMolecule.scss';

export interface TrackCardMoleculeProps {
  title: string;
  /** Mono readout on the right of the header (e.g. "at_risk", "15 · Minimal"). */
  value: React.ReactNode;
  titlePeek?: string;
  titlePayload?: InspectorPayload;
  onInspect?: (payload: InspectorPayload) => void;
  pinned?: boolean;
  onPin?: () => void;
  /** Cadence caption + in-place check-in — only present on cadence-bearing tracks. */
  footer?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}

const TrackCardMolecule: React.FC<TrackCardMoleculeProps> = ({
  title,
  value,
  titlePeek,
  titlePayload,
  onInspect,
  pinned,
  onPin,
  footer,
  children,
  className,
}) => {
  const { t } = useTranslation();

  return (
    <div className={['track-card-molecule', className].filter(Boolean).join(' ')}>
      <div className="track-card-molecule__header">
        <InspectableMolecule
          payload={titlePayload}
          onInspect={onInspect}
          peek={titlePeek}
          aria-label={title}
          className="track-card-molecule__title"
        >
          {title}
        </InspectableMolecule>
        {onPin && (
          <button
            type="button"
            className={['track-card-molecule__pin', pinned && 'track-card-molecule__pin--active'].filter(Boolean).join(' ')}
            onClick={onPin}
            aria-label={pinned
              ? t('trajectory.track.unpin', { defaultValue: 'Pinned to this glimpse — unpin to hide' })
              : t('trajectory.track.pin', { defaultValue: 'Pin this track to your glimpse' })}
            title={pinned
              ? t('trajectory.track.unpin', { defaultValue: 'Pinned to this glimpse — unpin to hide' })
              : t('trajectory.track.pin', { defaultValue: 'Pin this track to your glimpse' })}
          >
            <GlyphAtom glyph="lucide:pin" />
          </button>
        )}
        <span className="track-card-molecule__value">{value}</span>
      </div>
      <div className="track-card-molecule__body">{children}</div>
      {footer && <div className="track-card-molecule__footer">{footer}</div>}
    </div>
  );
};

TrackCardMolecule.displayName = 'TrackCardMolecule';

export default TrackCardMolecule;
